// Single source of truth for the workspace API base URL.
//
// Wraps `@symbo.ls/channels` — the canonical platform-wide channel
// registry — so every consumer that hits `/workspace/*` (SDK init in
// sdk.js, coreHeaders.js, the
// workspace-project SDK adapter in envs.js) resolves to the same URL
// derived from a single channel name. Channels (`local`, `next`,
// `staging`, `upcoming`, `production`, ...) are defined in
// `server/packages/channels/channels.json`.
//
// Hostname → channel autodetection is computed by walking the channel
// registry's shell hostnames at runtime — there's no per-host map in
// this file. If a new channel adds a `shell` URL to channels.json,
// this resolver auto-recognises it.
//
// Local dev: the `local` channel's shell is `http://my.localhost:1355`,
// so reaching the workspace via that host (the canonical portless URL)
// autodetects the `local` channel and points at `http://localhost:8080`.
// Hitting raw `localhost:<parcel-port>` falls through to the registry's
// `defaultChannel`. Either case is overridable per-tab with
// `localStorage.symbols_api_channel = '<channel>'`.
//
// Precedence (first non-null wins):
//   1. `?channel=<known-channel>` on local hosts — per-navigation override.
//   2. `SYMBOLS_API_CHANNEL` build-time env pin — local hosts only; the dev
//      launcher (scripts/start.js) defaults it to `development`, so plain
//      `bun start` talks to dev.api. `SYMBOLS_API_CHANNEL=local bun start`
//      opts into the local server.
//   3. `window.__SYMBOLS_ENV.WORKSPACE_API_URL`  — explicit URL (LOCAL hosts only),
//      escape hatch for ngrok / mirrors / staging proxies.
//   4. `window.__SYMBOLS_ENV.NODE_ENV`          — channel name override.
//   5. `localStorage.symbols_workspace_api`     — explicit URL (LOCAL hosts only).
//   6. `localStorage.symbols_api_channel`       — channel name (LOCAL hosts only).
//   7. Hostname autodetect (channel registry walk).
//   8. `apiUrl()` from `@symbo.ls/channels`     — registry's own
//      resolution (NODE_ENV → defaultChannel).
//
// URL override — LOCAL DEV HOSTS ONLY (localhost / *.localhost):
//   ?apiUrl=http://localhost:8080  → use a local API for this page load
// It is never persisted, never accepts a remote API, and is ignored on every
// other host: a link must never choose where the bearer token goes (P0,
// 2026-10-08 — `?apiUrl=` used to be honoured and persisted on every host but
// my.symbols.app). Any override an older build persisted is cleared off a
// local host.

import {
  apiUrl as channelApiUrl,
  shellUrl as channelShellUrl,
  previewUrl as channelPreviewUrl,
  isKnownChannel,
  channelNames
} from '@symbo.ls/channels'

// FA204 / frank trap — every helper in this file used to live at module
// scope, referenced bare from this file's one export, `workspaceApiBase`
// below. `_queryApplied` is genuine cross-call state (a once-per-page-load
// idempotency flag) and is re-homed onto `window.__workspaceApiBaseState`
// per the pointerCapability.js template; every other helper here is pure
// (or cheap to recompute — `_PROD_HOSTS` is a handful of Set entries built
// off the channel registry) and is inlined directly into the export below.

export function workspaceApiBase() {
  if (typeof window === 'undefined') return channelApiUrl()

  // Build-time NODE_ENV — Parcel inlines `process.env.NODE_ENV` as a
  // literal string at build time when it's accessed directly (no
  // `typeof process` guard, no destructuring). The `try`/`catch` is only
  // there for the rare bundler that doesn't do the substitution — at
  // runtime in the browser `process` is undefined and the access would
  // throw. This is the authoritative env for hostname-less surfaces like
  // raw `localhost`.
  let BUILD_ENV = null
  try {
    BUILD_ENV = process.env.NODE_ENV || null
  } catch (_) {
    /* no process — leave null */
  }

  // Build-time channel pin — the dev launcher (scripts/start.js) exports
  // SYMBOLS_API_CHANNEL (`development` by default, so the team's plain
  // `bun start` talks to dev.api; `SYMBOLS_API_CHANNEL=local bun start`
  // opts into the local server). Parcel inlines it as a literal exactly
  // like NODE_ENV above. The pin lives as long as that dev-server process
  // — every tab and reload of the run keeps it, a restart without the
  // variable drops it — which is why it is exempt from the stale-pin
  // guard below: unlike a localStorage / __SYMBOLS_ENV remote pin it
  // cannot outlive the intent that set it (persisted remote pins could,
  // and that was the dev.api "Failed to fetch" bug). Consulted on LOCAL
  // hosts only, so a deployed artifact accidentally built with the
  // variable set keeps resolving by hostname.
  let BUILD_CHANNEL = null
  try {
    BUILD_CHANNEL = process.env.SYMBOLS_API_CHANNEL || null
  } catch (_) {
    /* no process — leave null */
  }

  const hostnameOf = (url) => {
    if (!url) return null
    try {
      return new URL(url).hostname
    } catch {
      return null
    }
  }

  // Local-dev origins: `localhost` and every `*.localhost` portless sub-app
  // host (my.localhost, workspace.localhost, canvas.localhost,
  // api.localhost, …). On such an origin the `*.localhost → local`
  // autodetect is authoritative; a stale REMOTE pin (an old `?apiUrl=`
  // capture, or symbols_workspace_api / symbols_api_channel left pointing
  // at a non-local tier) must NOT strand the single SDK instance on an
  // unreachable API — that is the dev.api "Failed to fetch" bug. A pin
  // naming a LOCAL host (localhost:8080, the api.localhost:1355 h2 alias)
  // is still honored.
  const isLocalHostname = (host) =>
    host === 'localhost' || (typeof host === 'string' && host.endsWith('.localhost'))
  const isLocalUrl = (url) => {
    const h = hostnameOf(url)
    return h ? isLocalHostname(h) : false
  }

  // Autodetect channel from current hostname by walking the channel
  // registry. Three-pass:
  //   1. Exact hostname match against each channel's shell URL hostname
  //      (covers `dev.my.symbols.app`, `next.my.symbols.app`, etc.).
  //   2. Suffix match against each channel's shell + preview hostname.
  //      Covers wildcard project previews like
  //      `<owner>--<key>.dev.preview.symbols.app` and any other
  //      sub-subdomain that lives under a registered channel host —
  //      without this, project previews fall through to defaultChannel
  //      = production and call `next.api.symbols.app` instead of the
  //      tier-matching API.
  //   3. If still no match, parse the leading subdomain label and check
  //      if it's a known channel name (covers canvas/workspace shell
  //      variants like `dev.canvas.symbols.app` that aren't in
  //      channels.json's `shell` field).
  //
  // `dev` is aliased to `development` because that's what the channel is
  // named in the registry — the only label-renaming compromise; every
  // other channel uses the same name in hostname and registry.
  const hostToChannel = (host) => {
    if (!host) return null

    // PR-preview surfaces (`<id>.pr.my.symbols.app`, `<id>.pr.api.symbols.app`)
    // are ephemeral and not registry channels; without this they suffix-match
    // the production shell host `my.symbols.app` (Pass 2 below) and wrongly call
    // the prod API. Point them at the development tier (dev.api.symbols.app).
    if (/\.pr\.[a-z]+\.symbols\.app$/i.test(host)) return 'development'

    // The comm host (`comm.symbols.app`, `<tier>.comm.symbols.app` — commHost.js)
    // serves the SAME bucket as the workspace host of its tier, so it talks to
    // that tier's API: read it as its `my` twin and let the walk below match.
    // Bare `comm.symbols.app` matched nothing and failed closed. The label rule
    // is restated here (FA204 — this function is self-contained).
    const commTwin = /^((?:[a-z0-9-]+\.)*)comm?\.symbols\.app$/i.exec(host)
    if (commTwin) host = `${commTwin[1]}my.symbols.app`

    // The portless dev proxy serves EVERY `*.localhost` sub-app host on :1355
    // (my.localhost, workspace.localhost, canvas.localhost, preview.localhost, …) —
    // all local dev, all api → http://localhost:8080. Only `my.localhost` matched
    // the shell-host walk below (it IS the local channel's shell); the sibling
    // sub-app hosts fell through to the build's NODE_ENV / defaultChannel and hit
    // the wrong (dev/prod) API — Failed-to-fetch when that tier isn't reachable.
    // Route any local-dev host (bare `localhost` on a direct Parcel port, e.g.
    // `:4506`, OR any `*.localhost` portless sub-app host) to the local channel.
    //
    // Bug fixed 2026-08-15: this used to be `host.endsWith('.localhost')`,
    // which is false for bare `localhost` (`'localhost'.endsWith('.localhost')
    // === false` — no leading label). A bare `http://localhost:4506` tab fell
    // through every pass below and landed on the BUILD_ENV / defaultChannel
    // fallback (`dev.api.symbols.app`), while `:1355` (which routes through
    // `my.localhost`) correctly resolved to `local`. Measured: `:1355` sent 14
    // calls to `localhost:8080` and 0 to `dev.api`; `:4506` sent 0 to
    // `localhost:8080` (all silently went remote instead). This is what
    // produced a convincing FAKE auth bug — a `:4506` tab redirected to
    // `/login` (its SDK was pointed at a server it was never signed into)
    // while a `:1355` tab of the same session stayed signed in.
    //
    // `isLocalHostname` (above) already covered bare `localhost` correctly —
    // it just wasn't consulted here, only for gating whether a STORED pin is
    // honored (the `onLocal`/`ok()` guard below). Reusing it here is the fix:
    // the two local-host definitions in this file can never drift apart again.
    if (isLocalHostname(host) && isKnownChannel('local')) return 'local'

    // Build a longest-host-first list of (channel, host) tuples covering
    // both shell + preview surfaces, so specific hosts beat generic ones
    // when both could match.
    const tiers = []
    for (const name of channelNames) {
      for (const url of [channelShellUrl(name), channelPreviewUrl(name)]) {
        const h = hostnameOf(url)
        if (h) tiers.push({ name, h })
      }
    }
    tiers.sort((a, b) => b.h.length - a.h.length)

    // Pass 1: exact match (e.g. `dev.my.symbols.app`).
    for (const { name, h } of tiers) {
      if (host === h) return name
    }
    // Pass 2: suffix match (`*.dev.preview.symbols.app` → development).
    // Anchored at a label boundary so `evil-dev.preview.symbols.app`
    // doesn't accidentally claim the dev channel.
    for (const { name, h } of tiers) {
      if (host.endsWith('.' + h)) return name
    }

    // Pass 3: prefix label. `<env>.<*>.symbols.app` — the leading label
    // is the channel name (or `dev` aliased to `development`).
    const labels = host.split('.')
    if (labels.length < 4) return null // bare apex like `my.symbols.app`
    const prefix = labels[0]
    if (prefix === 'dev') return 'development'
    if (isKnownChannel(prefix)) return prefix
    return null
  }

  const queryParam = (name) => {
    try {
      return new URLSearchParams(window.location.search).get(name)
    } catch (_) {
      return null
    }
  }

  // Apply a `?apiUrl=...` query-param override into localStorage so the
  // rest of the precedence chain (and every subsequent page load) honors
  // it. Designed for testing how a server PR-preview deploy
  // (e.g. https://332.pr.api.symbols.app — see server/workers/pr-preview)
  // affects the live UI without rebuilding the frontend.
  //
  // Behavior:
  //   ?apiUrl=https://332.pr.api.symbols.app  → store + use
  //   ?apiUrl=clear  (or  ?apiUrl=  blank)    → remove the override
  // Non-prod hosts only — prod is hard-locked.
  //
  // Idempotent: only applies once per page load (URL param is the trigger,
  // after which the localStorage key carries the override across navs).
  // SECURITY (P0, 2026-10-08): a URL never chooses the API the SDK sends the
  // bearer token to. `?apiUrl=` used to be honoured AND persisted on every
  // host but my.symbols.app — `https://<white-label>/?apiUrl=https://evil`
  // pointed the stored token at the attacker. Now it counts only on a LOCAL dev
  // host, only for a LOCAL API, only for this page load (never persisted), and
  // any override an older build persisted is ignored and CLEARED off a local
  // host (workspaceApiBase.noUrlOverride.test.js).
  const onLocalHost = isLocalHostname(window.location.hostname)
  if (!onLocalHost) {
    try {
      window.localStorage.removeItem('symbols_workspace_api')
      window.localStorage.removeItem('symbols_api_channel')
    } catch (_) {
      /* sandboxed storage — nothing persisted to clear */
    }
  }
  const queryApiUrl = () => {
    if (!onLocalHost) return null
    const raw = queryParam('apiUrl')
    if (raw == null) return null
    try {
      const u = new URL(raw.trim())
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
      return isLocalHostname(u.hostname) ? u.origin : null
    } catch (_) {
      return null
    }
  }

  const localQueryChannel = () => {
    if (!isLocalHostname(window.location.hostname)) return null
    const channel = queryParam('channel')
    return channel && isKnownChannel(channel) ? channel : null
  }

  // A local dev host may name a LOCAL API in the URL, for this load only.
  const queryUrl = queryApiUrl()
  if (queryUrl) return queryUrl
  // A channel named in the current URL is an intentional, per-navigation
  // choice, not a stale persisted pin. Honor it on local shells so developers
  // can use a remote API when the local server is not running. Stored remote
  // channels remain subject to the local-host guard below.
  const queryChannel = localQueryChannel()
  if (queryChannel) return channelApiUrl(queryChannel)
  // Build-time SYMBOLS_API_CHANNEL pin (see the BUILD_CHANNEL note above).
  // After the query param — a channel named in the URL is a deliberate
  // per-navigation choice and must beat the process-wide default. `dev`
  // aliases to `development`, mirroring hostToChannel's label rule.
  if (BUILD_CHANNEL && isLocalHostname(window.location.hostname)) {
    const pinned = BUILD_CHANNEL === 'dev' ? 'development' : BUILD_CHANNEL
    if (isKnownChannel(pinned)) return channelApiUrl(pinned)
  }
  // On a local-dev origin (localhost / *.localhost) the hostname autodetect
  // MUST win over a stale REMOTE pin: an old `?apiUrl=` capture or a
  // symbols_workspace_api / symbols_api_channel left on a non-local tier would
  // otherwise freeze the single SDK instance on an unreachable API
  // (dev.api "Failed to fetch"), silently overriding the `*.localhost → local`
  // rule below. `ok(url)` returns the url only when it's allowed on this origin
  // (any host off-local; only local hosts on-local), else null → fall through
  // to the next resolver. A LOCAL pin (api.localhost h2 alias, localhost:8080)
  // is still honored.
  const onLocal = isLocalHostname(window.location.hostname)
  const ok = (url) => (!onLocal || isLocalUrl(url) ? url : null)
  // Explicit URL override (highest precedence — direct URL beats channel).
  // a page-injected URL override is a LOCAL dev escape hatch only
  const runtimeUrl = window.__SYMBOLS_ENV?.WORKSPACE_API_URL
  if (onLocal && runtimeUrl && ok(runtimeUrl)) return runtimeUrl
  // Channel name override at runtime.
  const runtimeChannel = window.__SYMBOLS_ENV?.NODE_ENV
  if (runtimeChannel) {
    const u = channelApiUrl(runtimeChannel)
    if (ok(u)) return u
  }
  // localStorage — explicit URL flag survives reloads.
  try {
    // read on a local dev host only (cleared above anywhere else)
    const lsUrl = onLocal ? window.localStorage.getItem('symbols_workspace_api') : null
    if (lsUrl && ok(lsUrl)) return lsUrl
    const lsChannel = onLocal ? window.localStorage.getItem('symbols_api_channel') : null
    if (lsChannel) {
      const u = channelApiUrl(lsChannel)
      if (ok(u)) return u
    }
  } catch (_) {
    /* sandbox contexts may throw */
  }
  // Hostname autodetect — walks the channel registry.
  const matched = hostToChannel(window.location.hostname)
  if (matched) return channelApiUrl(matched)

  // WHITELABEL-CHANNEL-DEFAULT-1 (tickets/opus.md) — the served channel
  // stamp, for hosts the registry cannot name: WHITE-LABEL CUSTOM DOMAINS.
  //
  // A custom domain is not in channels.json and never will be — customer #2
  // must cost ZERO deploys, which is the same principle that rules out an
  // env-list CORS entry and a per-customer OAuth console entry. So the walk
  // above misses and, before this, execution fell straight to the BUILD_ENV
  // literal below. Measured 2026-08-19: app.bellforge.eu therefore talked to
  // dev.api while its org, workspace and verified customDomains row all live
  // on STAGING (Nika ruled it stays there) — the lookup returned `data: null`
  // and the whole white-label chain failed closed for a third independent
  // reason.
  //
  // ⚠️ WHY THE STAMP IS THE ONLY HONEST SOURCE: the SAME artifact is deployed
  // to dev.my AND staging.my, so "which plane am I" is genuinely NOT knowable
  // from the bundle — only from where it was served. `deploy-workspace.sh`
  // uploads a separate copy per target and a custom domain is served BY that
  // target's backend (app.bellforge.eu → my-staging → the staging bucket), so
  // the stamp is inherited by every custom domain on that backend, forever,
  // with no per-customer config.
  //
  // ⚠️ AND WHY IT IS READ *HERE*, not first (Nika, 2026-08-19: "do not change
  // anything for symbols"): every platform host matches `hostToChannel` and
  // has already returned above, so this line is unreachable for
  // my.symbols.app / canvas / preview / marketplace. Reading the meta at
  // higher precedence would put a deploy-time artifact in front of a resolver
  // every Symbols surface depends on, for no gain — the two agree on those
  // hosts by construction. Until the deploy half stamps it, this is a no-op
  // and behaviour is byte-identical to today.
  // THE ONE INJECTED-IDENTITY ACCESSOR. A deployment names its plane in one
  // of two injected values, never in the bundle:
  //   • a parcel deploy stamps `<meta name="symbols-channel">` (above);
  //   • the mermaid runtime (the PUBLISHED shell, *.symbo.ls) writes
  //     `window.__SYMBOLS_CHANNEL__` before the runtime evaluates
  //     (@symbo.ls/mermaid deployIdentity.js).
  // An unknown name counts as none.
  const injectedChannel = () => {
    try {
      if (typeof document !== 'undefined' && document.querySelector) {
        const el = document.querySelector('meta[name="symbols-channel"]')
        const v = el && el.getAttribute('content')
        const channel = typeof v === 'string' ? v.trim() : ''
        if (channel && isKnownChannel(channel)) return channel
      }
    } catch (_) {
      /* no DOM — try the runtime injection */
    }
    const injected = window.__SYMBOLS_CHANNEL__
    return typeof injected === 'string' && isKnownChannel(injected) ? injected : null
  }
  const stamped = injectedChannel()
  if (stamped) return channelApiUrl(stamped)

  // Build-time NODE_ENV fallback — LOCAL HOSTS ONLY. The @symbo.ls/channels
  // dist's own `currentChannel()` is gated by a `typeof process` check that
  // fails in browsers, so plain `localhost` (no shell-host match) used the
  // build-time NODE_ENV literal Parcel substituted into our code. Off a local
  // host the value is not an identity: mermaid's process shim reads
  // `NODE_ENV: 'production'` on EVERY worker, dev included, so a published
  // dev shell resolved the production API from it.
  if (onLocal && BUILD_ENV && isKnownChannel(BUILD_ENV)) return channelApiUrl(BUILD_ENV)

  // FAIL CLOSED. No host rule and no injected identity: there is no honest
  // answer, and the registry default is production. '' makes getSDK serve
  // its null client (no backend reached) instead of talking to the wrong
  // plane (workspaceApiBase.failClosed.test.js).
  return ''
}

// The deployment's served channel — the same two injected values
// workspaceApiBase's own accessor reads (`<meta name="symbols-channel">`
// stamped by a parcel deploy, else the mermaid runtime's
// `window.__SYMBOLS_CHANNEL__`). Null when neither names a known channel. For
// callers that must know the PLANE on a host whose name says nothing about it
// (a customer's white-label domain): the org switcher's fallback shell host
// (components/AppOrgSwitcher.js, ruling b — a staging white-label host never
// falls back to the prod shell). A self-contained copy of the accessor inside
// workspaceApiBase (the FA204 frank trap: a bare sibling call is unresolvable
// in the emitted bundle) — keep the two equal.
export function deploymentChannel() {
  try {
    if (typeof document !== 'undefined' && document && document.querySelector) {
      const el = document.querySelector('meta[name="symbols-channel"]')
      const v = el && el.getAttribute('content')
      const channel = typeof v === 'string' ? v.trim() : ''
      if (channel && isKnownChannel(channel)) return channel
    }
  } catch (_) {
    /* no DOM — try the runtime injection */
  }
  const injected = typeof window !== 'undefined' ? window.__SYMBOLS_CHANNEL__ : undefined
  return typeof injected === 'string' && isKnownChannel(injected) ? injected : null
}
